import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../../store';
import { getAdminInferenceLogDetail, getAdminInferenceLogs, getAdminInferenceRequestTypes } from '../../api';
import { AdminInferenceLog, AdminInferenceLogDetail } from '../../types';
import { AdminLayout } from '../../components/admin/AdminLayout';

export function AdminInference() {
  const navigate = useNavigate();
  const { logId } = useParams<{ logId?: string }>();
  const { user, isAuthenticated, isLoading: authLoading } = useAuthStore();

  const [logs, setLogs] = useState<AdminInferenceLog[]>([]);
  const [logDetail, setLogDetail] = useState<AdminInferenceLogDetail | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(50);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [requestTypes, setRequestTypes] = useState<string[]>([]);
  const [filters, setFilters] = useState({
    requestType: '',
    search: '',
    errorsOnly: false,
  });

  // Debounce search input
  const [searchInput, setSearchInput] = useState('');
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !user?.is_admin)) {
      navigate('/', { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate, user]);

  // Load distinct request types on mount
  useEffect(() => {
    if (!authLoading && isAuthenticated && user?.is_admin) {
      getAdminInferenceRequestTypes()
        .then(data => setRequestTypes(data.requestTypes))
        .catch(() => {});
    }
  }, [authLoading, isAuthenticated, user?.is_admin]);

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAdminInferenceLogs(limit, page * limit, {
        requestType: filters.requestType || undefined,
        search: filters.search || undefined,
        errorsOnly: filters.errorsOnly,
      });
      setLogs(data.logs);
      setTotal(data.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  }, [filters.errorsOnly, filters.requestType, filters.search, limit, page]);

  const loadLogDetail = useCallback(async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAdminInferenceLogDetail(id);
      setLogDetail(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load log');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && isAuthenticated && user?.is_admin) {
      if (logId) {
        setLogDetail(null);
        void loadLogDetail(logId);
      } else {
        setLogDetail(null);
        void loadLogs();
      }
    }
  }, [authLoading, isAuthenticated, loadLogDetail, loadLogs, logId, user?.is_admin]);

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-dark-900">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!user?.is_admin) return null;

  if (logId && logDetail) {
    let messages: Array<{ role: string; content: string }> = [];
    try {
      messages = JSON.parse(logDetail.messages_json || '[]');
    } catch {
      messages = [];
    }

    return (
      <AdminLayout
        title="Inference Detail"
        breadcrumbs={[
          { label: 'Admin', to: '/admin' },
          { label: 'Inference', to: '/admin/inference' },
          { label: logId },
        ]}
        actions={<span className={`w-3 h-3 rounded-full ${logDetail.success ? 'bg-green-500' : 'bg-red-500'}`} />}
      >
        <div className="space-y-6">
          {error && (
            <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          <div className="bg-dark-800 border border-dark-600 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-dark-50 mb-4">Request Info</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <InfoItem label="Request Type" value={logDetail.request_type} />
              <InfoItem label="Agent" value={logDetail.agent_name || '-'} />
              <InfoItem label="Provider" value={logDetail.provider} />
              <InfoItem label="Model" value={logDetail.model} />
              <InfoItem label="Prompt Tokens" value={logDetail.prompt_tokens?.toLocaleString() || '-'} />
              <InfoItem label="Completion Tokens" value={logDetail.completion_tokens?.toLocaleString() || '-'} />
              <InfoItem label="Latency" value={logDetail.latency_ms ? `${logDetail.latency_ms}ms` : '-'} />
              <InfoItem label="Temperature" value={logDetail.temperature?.toString() || 'default'} />
            </div>
            {logDetail.world_id && (
              <div className="mt-4 pt-4 border-t border-dark-600">
                <Link to={`/admin/worlds/${logDetail.world_id}`} className="text-primary-400 hover:underline">
                  View World: {logDetail.world_title || logDetail.world_id}
                </Link>
              </div>
            )}
          </div>

          {!logDetail.success && logDetail.error_message && (
            <div className="bg-red-900/30 border border-red-700 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-red-400 mb-2">Error</h3>
              <pre className="text-red-200 whitespace-pre-wrap text-sm">{logDetail.error_message}</pre>
            </div>
          )}

          {logDetail.system_prompt && (
            <div className="bg-dark-800 border border-dark-600 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-dark-50 mb-4">System Prompt</h3>
              <pre className="bg-dark-700 rounded p-4 text-dark-200 whitespace-pre-wrap text-sm overflow-x-auto max-h-96 overflow-y-auto">
                {logDetail.system_prompt}
              </pre>
            </div>
          )}

          <div className="bg-dark-800 border border-dark-600 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-dark-50 mb-4">Messages ({messages.length})</h3>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`rounded p-3 ${
                    msg.role === 'user'
                      ? 'bg-primary-900/30 border border-primary-800'
                      : msg.role === 'assistant'
                        ? 'bg-dark-700'
                        : 'bg-dark-600'
                  }`}
                >
                  <div className="text-xs font-medium text-dark-400 uppercase mb-1">{msg.role}</div>
                  <pre className="text-dark-200 whitespace-pre-wrap text-sm">{msg.content}</pre>
                </div>
              ))}
            </div>
          </div>

          {logDetail.response_content && (
            <div className="bg-dark-800 border border-dark-600 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-dark-50 mb-4">Response</h3>
              <pre className="bg-dark-700 rounded p-4 text-dark-200 whitespace-pre-wrap text-sm overflow-x-auto max-h-96 overflow-y-auto">
                {logDetail.response_content}
              </pre>
            </div>
          )}

          {logDetail.tool_calls_json && (
            <div className="bg-dark-800 border border-dark-600 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-dark-50 mb-4">Tool Calls</h3>
              <pre className="bg-dark-700 rounded p-4 text-dark-200 whitespace-pre-wrap text-sm overflow-x-auto">
                {(() => {
                  try {
                    return JSON.stringify(JSON.parse(logDetail.tool_calls_json), null, 2);
                  } catch {
                    return logDetail.tool_calls_json;
                  }
                })()}
              </pre>
            </div>
          )}
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title="Inference Logs"
      breadcrumbs={[{ label: 'Admin', to: '/admin' }, { label: 'Inference' }]}
      actions={
        <div className="flex items-center gap-3">
          <input
            type="text"
            placeholder="Search prompts & responses..."
            value={searchInput}
            onChange={(e) => {
              const value = e.target.value;
              setSearchInput(value);
              clearTimeout(searchTimerRef.current);
              searchTimerRef.current = setTimeout(() => {
                setFilters(f => ({ ...f, search: value }));
                setPage(0);
              }, 300);
            }}
            className="bg-dark-700 border border-dark-600 rounded px-3 py-2 text-dark-50 placeholder-dark-400 w-64"
          />
          <select
            value={filters.requestType}
            onChange={(e) => {
              setFilters(f => ({ ...f, requestType: e.target.value }));
              setPage(0);
            }}
            className="bg-dark-700 border border-dark-600 rounded px-3 py-2 text-dark-50"
          >
            <option value="">All Request Types</option>
            {requestTypes.map(rt => (
              <option key={rt} value={rt}>{rt}</option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-dark-300">
            <input
              type="checkbox"
              checked={filters.errorsOnly}
              onChange={(e) => {
                setFilters(f => ({ ...f, errorsOnly: e.target.checked }));
                setPage(0);
              }}
              className="accent-primary-500"
            />
            Errors only
          </label>
        </div>
      }
    >
      <div>
        {error && (
          <div className="bg-red-900/50 border border-red-500 text-red-200 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        <div className="space-y-2">
          {loading ? (
            <div className="text-center text-dark-400 py-8">Loading...</div>
          ) : logs.length === 0 ? (
            <div className="text-center text-dark-400 py-8">No inference logs found</div>
          ) : (
            logs.map((log) => (
              <Link
                key={log.id}
                to={`/admin/inference/${log.id}`}
                className="block bg-dark-800 border border-dark-600 rounded-lg p-4 hover:border-dark-500 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${log.success ? 'bg-green-500' : 'bg-red-500'}`} />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-dark-50 font-medium">{log.request_type}</span>
                        {log.agent_name && <span className="text-dark-400 text-sm">({log.agent_name})</span>}
                      </div>
                      <div className="text-dark-400 text-sm">
                        {log.user_email} • {log.provider}/{log.model}
                        {log.world_title && ` • ${log.world_title}`}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-6 text-sm text-dark-400">
                    <span>{((log.prompt_tokens || 0) + (log.completion_tokens || 0)).toLocaleString()} tokens</span>
                    <span>{log.latency_ms || '-'}ms</span>
                    <span className="w-40 text-right">{new Date(log.created_at).toLocaleString()}</span>
                  </div>
                </div>
                {!log.success && log.error_message && (
                  <div className="mt-2 text-red-400 text-sm truncate">Error: {log.error_message}</div>
                )}
              </Link>
            ))
          )}
        </div>

        {total > limit && (
          <div className="flex items-center justify-between mt-4">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="btn-secondary disabled:opacity-50"
            >
              Previous
            </button>
            <div className="flex items-center gap-4">
              <span className="text-dark-400">Page {page + 1} of {Math.ceil(total / limit)}</span>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(0);
                }}
                className="bg-dark-700 border border-dark-600 rounded px-2 py-1 text-dark-50 text-sm"
              >
                <option value={30}>30 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
            </div>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={(page + 1) * limit >= total}
              className="btn-secondary disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-dark-400 text-sm">{label}</div>
      <div className="text-dark-50 font-medium">{value}</div>
    </div>
  );
}
